import type jsPDF from 'jspdf'
import type { autoTable, CellDef } from 'jspdf-autotable'

import { capitaliseWords } from 'utils/capitaliseWords'
import { stripHtml } from 'utils/stripHTML'
import colors from '../../../../tailwind.colors'

declare module 'jspdf' {
  interface jsPDF {
    autoTable: autoTable
    lastAutoTable: {
      finalY: number
    }
  }
}

export async function formatPrintData(
  rows: HTMLElement[],
  selectedOptions: string[],
  disclaimer?: string
): Promise<jsPDF> {
  const { jsPDF } = await import('jspdf')
  const { applyPlugin } = await import('jspdf-autotable')
  const { default: html2canvas } = await import('html2canvas')
  applyPlugin(jsPDF)

  const pageOrientation = selectedOptions.length > 2 ? 'landscape' : 'portrait'
  const doc = new jsPDF(pageOrientation)
  const { width: pageWidth, height: pageHeight } = doc.internal.pageSize
  const margin = { x: 15, y: 8 }

  doc.setFontSize(20)
  doc.setFont('helvetica', 'bold')
  doc.text('Compare Careers', margin.x, margin.y + 20)
  doc.setFontSize(12)
  doc.setLineHeightFactor(1.25)

  const groups = rows.reduce((acc, row) => {
    if (row.hasAttribute('data-snap')) {
      return acc
    } else if (row.children.length === 1) {
      acc.push([row as HTMLElement])
    } else {
      const currentGroup = acc[acc.length - 1]
      currentGroup.push(row as HTMLElement)
    }
    return acc
  }, [] as HTMLElement[][])

  const tableWidth = pageWidth - margin.x * 2
  const rowHeadingWidth = groups.reduce((acc, group) => {
    group.forEach((row) => {
      if (row.children.length > 1) {
        const cell = row.children[0] as HTMLElement
        const lines = cell.innerText.split('\n')
        lines.forEach((line) => {
          acc = Math.max(acc, doc.getTextWidth(line))
        })
      }
    })
    return acc
  }, 0)
  const cellWidth = (tableWidth - rowHeadingWidth) / selectedOptions.length

  const getHeaderText = (cell: HTMLElement) =>
    cell.children.length > 0
      ? Array.from(cell.children)
        .map((c: HTMLElement) => c.innerText)
        .join(' ')
      : cell.textContent ?? ''


  const getCellText = (cell: HTMLElement, level: number = 0): string => {
    if (cell.children.length > 0) {
      return Array.from(cell.children)
        .reduce((acc: string, c: HTMLElement) => {
          // Handle UL elements
          if (c.tagName === 'UL') {
            // Don't add extra newline for nested lists
            return acc + getCellText(c, level)
          }

          // Handle BR elements - ignore them to prevent unwanted line breaks
          else if (c.tagName === 'BR') {
            return acc + '\n'
          }

          // Handle P,H tags - ensure they end with newline
          else if (c.tagName === 'P' || c.tagName === 'H2' || c.tagName === 'H3' || c.tagName === 'H4' || c.tagName === 'H5') {
            const text = c.innerText.trim().replace(/\s+/g, ' ')
            return acc + text + '\n\n'
          }

          // Handle DIV containers with nested elements
          else if (c.tagName === 'DIV' && Array.from(c.children).some(x =>
            ['DIV', 'P', 'UL', 'OL'].includes(x.tagName))) {
            const content = getCellText(c, level)
            // Only add newline if content doesn't end with one
            return acc + content + (!content.endsWith('\n') ? '\n' : '')
          }

          // Handle list items at different levels
          else if (c.tagName === 'LI') {
            const indent = '   '.repeat(level)
            const bullet = '• '
            const hasNestedList = Array.from(c.children).some(x => x.tagName === 'UL')

            if (hasNestedList) {
              // Get the text content before the nested list, removing extra spaces and line breaks
              const textContent = Array.from(c.childNodes)
                .filter(node => node.nodeType === Node.TEXT_NODE)
                .map(node => node.textContent?.trim())
                .join(' ')
                .replace(/\s+/g, ' ')

              // Process nested content with increased level
              const nestedContent = Array.from(c.children)
                .filter(child => child.tagName === 'UL')
                .map(ul => getCellText(ul as HTMLElement, level + 1))
                .join('')

              return acc + indent + bullet + textContent + '\n' + nestedContent
            } else {
              // Clean up the text content by removing extra spaces and line breaks
              const cleanText = c.innerText
                .replace(/\s+/g, ' ')
                .trim()
              return acc + indent + bullet + cleanText + '\n'
            }
          }

          // Handle all other elements
          else {
            const text = c.innerText.trim()
            if (!text) return acc
            // Clean up text by removing extra spaces and line breaks
            const cleanText = text.replace(/\s+/g, ' ')

            // add line break after div 
            const cleanTextwithBreak = (cleanText.length > 0 && c.tagName == "DIV") ? cleanText + "\n" : cleanText

            return acc.length > 0 ?
              acc + (acc.endsWith('\n') ? cleanTextwithBreak : ' ' + cleanTextwithBreak) :
              cleanTextwithBreak
          }
        }, '')
    } else {
      // Clean up text for leaf nodes
      return cell.innerText?.trim().replace(/\s+/g, ' ') ?? ''
    }
  }

  const footnotes = groups.reduce((footnotes, group) => {
    const [head, ...body] = group.map((row) => {
      const rowData = Array.from(row.children).flatMap((cell, i, arr): CellDef => {
        const cellPadding = {
          top: 4,
          bottom: 3,
          horizontal: 4,
        }
        const valign = 'top'
        let content = (i == 0) ? getHeaderText(cell as HTMLElement) : getCellText(cell as HTMLElement)

        if (arr.length === 1) {
          return {
            content: content,
            styles: { fontStyle: 'bold', fontSize: 13, cellPadding, valign },
            colSpan: selectedOptions.length + 1,
          }
        } else if (i === 0) {
          const footnote = cell.querySelector<HTMLElement>('.data-info')

          if (footnote) {
            const text = getCellText(footnote)
            footnotes.push(text)
            content = content.slice(0, content.lastIndexOf('\n'))
            content += `^${footnotes.length}`
          }

          return {
            content,
            styles: {
              fontStyle: 'bold',
              cellPadding,
              valign: 'top',
              overflow: 'hidden',
            },
          }
        } else {
          const link = cell.querySelector<HTMLAnchorElement>('a')?.href
          if (link) {
            content += `\n${link}`
          }

          return {
            content,
            styles: {
              cellPadding:
                i === arr.length - 1
                  ? {
                    ...cellPadding,
                    horizontal: undefined,
                    left: cellPadding.horizontal,
                    right: cellPadding.horizontal * 1.5,
                  }
                  : cellPadding,
              valign,
              cellWidth,
            },
          }
        }
      })

      return rowData
    })

    const startY = doc.lastAutoTable.finalY ? doc.lastAutoTable.finalY + 5 : 35
    doc.autoTable({
      tableId: head[0].content?.toString() ?? 'undefined',
      startY,
      margin: { top: 22 },
      rowPageBreak: 'avoid',
      pageBreak: 'auto',
      head: [head],
      headStyles: {
        fillColor: colors['tri-service'].tint,
        textColor: colors.black['off-secondary'],
      },
      bodyStyles: {
        fillColor: colors.white.off,
        textColor: colors.black['off-secondary'],
      },
      alternateRowStyles: {
        fillColor: colors.white.DEFAULT,
        textColor: colors.black['off-secondary'],
      },
      body,
      didParseCell: (data) => {
        if (data.cell.text.length > 1 && data.cell.text[1].match(/^http.*$/)) {
          const link = data.cell.text.slice(1).join('')
          if (link) {
            data.cell.text = ['']
          }
        }
      },
      willDrawCell: (data) => {
        if (data.section === 'head' && data.pageNumber > 1 && data.cell.text[0].indexOf('continued') == -1) {
          data.cell.text[0] = `${data.cell.text[0]} (continued)`
        }
        const rawContent = (data.cell.raw as CellDef).content
        if (typeof rawContent === 'string' && rawContent.match(/^[A-Z- ]+$/)) {
          data.cell.text[0] = capitaliseWords(rawContent)
        } else if (typeof rawContent === 'string' && rawContent?.match(/\^\d+$/)) {
          data.cell.text[0] = rawContent.slice(0, rawContent.lastIndexOf('^'))
        }
      },
      didDrawCell: ({ cell }) => {
        const rawContent = (cell.raw as CellDef).content

        // Draw footnote index as superscript
        if (typeof rawContent === 'string' && rawContent?.match(/\^\d+$/)) {
          const footnoteIndex = rawContent.slice(rawContent.lastIndexOf('^'))
          doc.setFontSize(8)
          doc.text(
            footnoteIndex,
            cell.x + cell.contentWidth - cell.padding('left') - cell.padding('right') + 1,
            cell.y + cell.padding('top') + 1
          )
        }

        // Draw link with underline
        if (typeof rawContent === 'string' && rawContent?.match(/\nhttp/)) {
          const [text, url] = rawContent.split('\n')
          const { w, h } = doc.getTextDimensions(text)
          const x = cell.x + cell.padding('left')
          const y = cell.y + cell.padding('top') + h
          doc.setTextColor('#0000ff')
          doc.textWithLink(text, x, y - 1, { url })
          doc.setDrawColor('#0000ff')
          doc.line(x, y, x + w, y)
        }
      },
    })

    return footnotes
  }, [] as string[])

  doc.autoTable({
    theme: 'plain',
    startY: doc.lastAutoTable.finalY + 10,
    margin: { top: 20 },
    rowPageBreak: 'avoid',
    pageBreak: 'auto',
    head: [[{ content: 'Learn more', colSpan: 2 }]],
    bodyStyles: { fontSize: 9 },
    body: footnotes.map((footnote, index) => [{ content: `^${index + 1}.`, styles: { cellWidth: 12 } }, footnote]),
  })

  if (disclaimer) {
    doc.setLineHeightFactor(1.5)
    doc.autoTable({
      theme: 'plain',
      startY: doc.lastAutoTable.finalY + 20,
      margin: { top: 24 },
      rowPageBreak: 'avoid',
      pageBreak: 'avoid',
      body: [[stripHtml(disclaimer)]],
      bodyStyles: { fontSize: 9, fontStyle: 'italic', textColor: '#595959' },
    })
    doc.setLineHeightFactor(1.25)
  }

  // Add footer text to every page
  const timestamp = getTimestamp()
  const logo = document.querySelector<HTMLImageElement>('.logo-wrapper img')

  for (let i = 1; i <= doc.getNumberOfPages(); i++) {
    if (logo) {
      const imgFile = await html2canvas(logo, { scale: 5 })
      doc.addImage(imgFile, 'SVG', margin.x, margin.y, 41.5, 10)
    }

    doc.setPage(i)
    doc.setFontSize(8)
    // Add timestamp and page number
    doc.text(timestamp, margin.x, pageHeight - margin.y, { align: 'left' })
    doc.text(`Page ${i} of ${doc.getNumberOfPages()}`, pageWidth - margin.x, pageHeight - margin.y, { align: 'right' })
  }

  return doc
}

function getTimestamp() {
  const date = new Date()
  const padLeft = (num: number, len = 2, chr = `0`) => `${num}`.padStart(len, chr)

  return `${padLeft(date.getDate())}/${padLeft(date.getMonth() + 1)}/${date.getFullYear()} ${padLeft(date.getHours())}:${padLeft(date.getMinutes())}`
}
